import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, FileUp, Upload } from 'lucide-react';
import clsx from 'clsx';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog } from '@/components/common/Modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { Pagination } from '@/components/tables/Pagination';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { importsApi } from '@/services/api/endpoints/imports';
import { queryKeys } from '@/services/api/queryKeys';
import type { ImportPreview, ImportRowError, ImportType } from '@/types/importing';
import { isApiError } from '@/utils/ApiError';
import { formatDateTime, orDash } from '@/utils/format';

interface SampleRow {
  key: string;
  values: Record<string, string>;
}

interface ErrorRow {
  key: string;
  error: ImportRowError;
}

/**
 * Spreadsheet import: pick a type → upload a CSV → preview → confirm.
 *
 * <p>Nothing here checks a row. Every count on this page — total, valid,
 * errors, imported — is the server's own; the sample and the error list are
 * both truncated by the server, so the page never counts them to produce a
 * figure. A refusal (expired preview, rows that stopped passing since the
 * preview, a file already imported) is shown exactly as the server worded it.
 *
 * <p>A file with errors imports only after the user ticks the explicit
 * "skip the rows with errors" box, and every commit goes through a confirm.
 */
export function ImportPage() {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [selectedType, setSelectedType] = useState<ImportType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportPreview | null>(null);
  const [skipAcknowledged, setSkipAcknowledged] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState<{ message: string; requestId?: string } | null>(null);

  const [historyPage, setHistoryPage] = useState(0);
  const [historySize, setHistorySize] = useState(20);

  const types = useQuery({
    queryKey: queryKeys.imports.types,
    queryFn: () => importsApi.types(),
  });

  const historyParams = { page: historyPage, size: historySize };
  const history = useQuery({
    queryKey: queryKeys.imports.history(historyParams),
    queryFn: () => importsApi.history(historyParams),
  });

  const invalidateHistory = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.imports.all });
  };

  function report(error: unknown) {
    handleApiError(error);
    setRefusal(
      isApiError(error)
        ? { message: error.message, requestId: error.requestId }
        : { message: 'Something went wrong. Please try again.' },
    );
  }

  function resetFile() {
    setFile(null);
    setFileInputKey((key) => key + 1);
    setPreview(null);
    setSkipAcknowledged(false);
    setConfirming(false);
    setRefusal(null);
  }

  function chooseType(type: ImportType) {
    if (selectedType?.type !== type.type) {
      resetFile();
    }
    setResult(null);
    setSelectedType(type);
  }

  function startOver() {
    resetFile();
    setResult(null);
  }

  const template = useMutation({
    mutationFn: (type: string) => importsApi.templateCsv(type),
    onError: (error) => handleApiError(error),
  });

  const errorsCsv = useMutation({
    mutationFn: (id: string) => importsApi.errorsCsv(id),
    onError: (error) => handleApiError(error),
  });

  const validate = useMutation({
    mutationFn: ({ type, upload }: { type: string; upload: File }) => importsApi.validate(type, upload),
    onMutate: () => {
      setRefusal(null);
      setPreview(null);
      setSkipAcknowledged(false);
    },
    onSuccess: (data) => {
      setPreview(data);
      invalidateHistory();
    },
    onError: (error) => report(error),
  });

  const commit = useMutation({
    mutationFn: (job: ImportPreview) => importsApi.commit(job.importId, job.errorRows > 0),
    onMutate: () => setRefusal(null),
    onSuccess: (data) => {
      setConfirming(false);
      setPreview(null);
      setResult(data);
      toast.success(`Imported ${orDash(data.importedRows)} rows from ${data.fileName}`);
      invalidateHistory();
    },
    onError: (error) => {
      setConfirming(false);
      report(error);
    },
  });

  const labelFor = (importType: string) =>
    (types.data ?? []).find((row) => row.type === importType)?.label ?? importType;

  // Sample columns follow the type's template; fall back to the sample's own keys.
  const previewType =
    preview === null ? null : (types.data ?? []).find((row) => row.type === preview.importType) ?? selectedType;
  const sampleColumnNames =
    previewType?.columns.map((column) => column.name) ?? Object.keys(preview?.sample[0] ?? {});

  const sampleColumns: Column<SampleRow>[] = sampleColumnNames.map((name) => ({
    key: name,
    header: name,
    render: (row) => <span className="whitespace-nowrap text-[13px] text-ink">{orDash(row.values[name])}</span>,
  }));

  const sampleRows: SampleRow[] = (preview?.sample ?? []).map((values, index) => ({
    key: String(index),
    values,
  }));

  const errorColumns: Column<ErrorRow>[] = [
    {
      key: 'row',
      header: 'Row',
      className: 'text-right',
      render: (row) => <span className="tabular-nums text-ink">{row.error.rowNumber}</span>,
    },
    {
      key: 'column',
      header: 'Column',
      render: (row) => (
        <span className="whitespace-nowrap font-mono text-[12px]">{row.error.column ?? 'Whole row'}</span>
      ),
    },
    {
      key: 'message',
      header: 'Message',
      render: (row) => <span className="text-[13px] text-ink">{row.error.message}</span>,
    },
  ];

  const errorRows: ErrorRow[] = (preview?.errors ?? []).map((error, index) => ({
    key: `${error.rowNumber}-${error.column ?? ''}-${index}`,
    error,
  }));

  const historyColumns: Column<ImportPreview>[] = [
    {
      key: 'file',
      header: 'File',
      render: (row) => (
        <span>
          <span className="block max-w-[16rem] truncate text-sm font-semibold text-ink">{row.fileName}</span>
          <span className="block text-[11px] text-subtle">{labelFor(row.importType)}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.status === 'COMMITTED' ? (
          <Badge tone="success">
            <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
            imported
          </Badge>
        ) : (
          <StatusBadge status={row.status} />
        ),
    },
    {
      key: 'total',
      header: 'Rows',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.totalRows}</span>,
    },
    {
      key: 'valid',
      header: 'Valid',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.validRows}</span>,
    },
    {
      key: 'errors',
      header: 'Errors',
      className: 'text-right',
      render: (row) => (
        <span className={clsx('tabular-nums', row.errorRows > 0 && 'font-semibold text-danger')}>
          {row.errorRows}
        </span>
      ),
    },
    {
      key: 'imported',
      header: 'Imported',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{orDash(row.importedRows)}</span>,
    },
    {
      key: 'when',
      header: 'When',
      render: (row) => (
        <span className="block whitespace-nowrap text-[13px]">
          {formatDateTime(row.committedAt ?? row.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        row.errorRows > 0 ? (
          <Button
            size="sm"
            variant="secondary"
            loading={errorsCsv.isPending && errorsCsv.variables === row.importId}
            onClick={() => errorsCsv.mutate(row.importId)}
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Errors CSV
          </Button>
        ) : null,
    },
  ];

  const needsSkip = preview !== null && preview.errorRows > 0;
  const canCommit =
    preview !== null && preview.validRows > 0 && (!needsSkip || skipAcknowledged) && !commit.isPending;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Import Data"
        description="Upload a CSV, check what the server found, then confirm. Nothing is written until you confirm."
        icon={<FileUp className="h-5 w-5" aria-hidden="true" />}
      />

      {result !== null ? (
        /* ---- success ------------------------------------------------- */
        <Card className="animate-fade-in p-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-success-soft text-success">
            <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
          </div>
          <h2 className="text-base font-semibold text-ink">Import complete</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted">
            <span className="font-semibold text-ink tabular-nums">{orDash(result.importedRows)}</span>{' '}
            {labelFor(result.importType).toLowerCase()} rows imported from{' '}
            <span className="font-medium text-ink">{result.fileName}</span>
            {result.committedAt !== null ? ` on ${formatDateTime(result.committedAt)}` : ''}.
          </p>
          {result.errorRows > 0 && (
            <p className="mx-auto mt-1 max-w-md text-sm text-muted">
              {result.errorRows} rows with errors were skipped.
            </p>
          )}
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {result.errorRows > 0 && (
              <Button
                variant="secondary"
                loading={errorsCsv.isPending}
                onClick={() => errorsCsv.mutate(result.importId)}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Download skipped rows' errors (CSV)
              </Button>
            )}
            <Button onClick={startOver}>Import another file</Button>
          </div>
        </Card>
      ) : (
        <>
          {/* ---- 1. type ------------------------------------------------ */}
          <Card>
            <CardHeader title="1. What are you importing?" description="Only the types you are allowed to create are listed." />
            {types.isPending ? (
              <LoadingState label="Loading import types" />
            ) : types.isError ? (
              <ErrorState error={types.error} onRetry={() => void types.refetch()} />
            ) : types.data.length === 0 ? (
              <EmptyState
                title="Nothing you can import"
                description="Importing a kind of record needs permission to create it. Ask an administrator if you expected to see a type here."
              />
            ) : (
              <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
                {types.data.map((type) => {
                  const active = selectedType?.type === type.type;
                  return (
                    <button
                      key={type.type}
                      type="button"
                      onClick={() => chooseType(type)}
                      aria-pressed={active}
                      className={clsx(
                        'border px-4 py-3 text-left transition-colors',
                        active
                          ? 'border-accent bg-accent-soft text-accent-soft-fg'
                          : 'border-line bg-bg text-ink hover:border-line-strong',
                      )}
                    >
                      <span className="block text-sm font-semibold">{type.label}</span>
                      <span className="mt-0.5 block text-[12px] text-muted">
                        {type.columns.length} columns · up to {type.maxRows} rows
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedType !== null && (
              <div className="border-t border-line p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-muted">
                    Columns for <span className="font-semibold text-ink">{selectedType.label}</span>.{' '}
                    <span className="text-danger">*</span> marks a required column.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={template.isPending}
                    onClick={() => template.mutate(selectedType.type)}
                  >
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                    Download template
                  </Button>
                </div>
                <ul className="mt-3 divide-y divide-line border border-line">
                  {selectedType.columns.map((column) => (
                    <li key={column.name} className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-baseline sm:gap-4">
                      <span className="shrink-0 font-mono text-[12px] text-ink sm:w-48">
                        {column.name}
                        {column.required && (
                          <span className="ml-1 text-danger" aria-label="required">
                            *
                          </span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1 text-[13px] text-muted">{column.description}</span>
                      {column.example !== '' && (
                        <span className="shrink-0 text-[12px] text-subtle">e.g. {column.example}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          {/* ---- 2. upload ---------------------------------------------- */}
          {selectedType !== null && (
            <Card>
              <CardHeader
                title="2. Upload the file"
                description={`A .csv file with the template's header row, up to ${selectedType.maxRows} rows. Checking it writes nothing.`}
              />
              <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
                <label className="min-w-0 flex-1">
                  <span className="sr-only">CSV file</span>
                  <input
                    key={fileInputKey}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(event) => {
                      setFile(event.target.files?.[0] ?? null);
                      setPreview(null);
                      setSkipAcknowledged(false);
                      setRefusal(null);
                    }}
                    className="block w-full text-sm text-muted file:mr-3 file:border file:border-line file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink"
                  />
                </label>
                <Button
                  disabled={file === null}
                  loading={validate.isPending}
                  onClick={() => {
                    if (file !== null) {
                      validate.mutate({ type: selectedType.type, upload: file });
                    }
                  }}
                >
                  <Upload className="h-4 w-4" aria-hidden="true" />
                  Check file
                </Button>
              </div>
            </Card>
          )}

          {refusal !== null && <Refusal message={refusal.message} requestId={refusal.requestId} />}

          {/* ---- 3. preview --------------------------------------------- */}
          {preview !== null && (
            <Card>
              <CardHeader
                title="3. Check the preview"
                description={`${preview.fileName} · ${labelFor(preview.importType)}${
                  preview.expiresAt !== null ? ` · preview expires ${formatDateTime(preview.expiresAt)}` : ''
                }`}
              />
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
                <Figure label="Rows in file" value={preview.totalRows} />
                <Figure label="Valid" value={preview.validRows} tone="success" />
                <Figure label="With errors" value={preview.errorRows} tone={preview.errorRows > 0 ? 'danger' : 'neutral'} />
              </div>

              <div className="border-t border-line">
                <h3 className="px-4 pt-4 text-sm font-semibold text-ink">Sample rows</h3>
                <p className="px-4 pb-2 text-[12px] text-muted">
                  The first rows as they will be imported, after the server tidied them.
                </p>
                <DataTable
                  columns={sampleColumns}
                  rows={sampleRows}
                  rowKey={(row) => row.key}
                  emptyTitle="No valid rows to show"
                  emptyDescription="Every row in this file has an error."
                />
              </div>

              {preview.errorRows > 0 && (
                <div className="border-t border-line">
                  <div className="flex flex-wrap items-start justify-between gap-3 px-4 pb-2 pt-4">
                    <div>
                      <h3 className="text-sm font-semibold text-ink">Errors</h3>
                      <p className="text-[12px] text-muted">
                        Row numbers count the header as row 1. Up to 500 are listed here; the CSV has every one.
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={errorsCsv.isPending}
                      onClick={() => errorsCsv.mutate(preview.importId)}
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden="true" />
                      Download all errors (CSV)
                    </Button>
                  </div>
                  <DataTable
                    columns={errorColumns}
                    rows={errorRows}
                    rowKey={(row) => row.key}
                    emptyTitle="No error details"
                    emptyDescription="Download the errors CSV for the full list."
                  />
                </div>
              )}

              {/* ---- 4. confirm ------------------------------------------ */}
              <div className="space-y-3 border-t border-line bg-surface-2 p-4">
                {preview.validRows === 0 ? (
                  <p className="text-sm text-muted">
                    There is nothing valid to import. Fix the file and upload it again.
                  </p>
                ) : (
                  needsSkip && (
                    <label className="flex items-start gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={skipAcknowledged}
                        onChange={(event) => setSkipAcknowledged(event.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 border-line"
                      />
                      Import only the {preview.validRows} valid rows and skip the {preview.errorRows} rows with
                      errors
                    </label>
                  )
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="secondary" onClick={resetFile}>
                    Choose another file
                  </Button>
                  <Button disabled={!canCommit} onClick={() => setConfirming(true)}>
                    Import {preview.validRows} rows
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ---- history ---------------------------------------------------- */}
      <Card>
        <CardHeader title="Import history" description="Every file checked or imported, newest first." />
        <DataTable
          columns={historyColumns}
          rows={history.data?.data}
          rowKey={(row) => row.importId}
          loading={history.isFetching}
          error={history.error}
          onRetry={() => void history.refetch()}
          emptyTitle="No imports yet"
          emptyDescription="Files you check or import appear here."
        />
        <Pagination
          pagination={history.data?.pagination}
          onPageChange={setHistoryPage}
          onSizeChange={(size) => {
            setHistorySize(size);
            setHistoryPage(0);
          }}
        />
      </Card>

      <ConfirmDialog
        open={confirming && preview !== null}
        title={preview === null ? '' : `Import ${preview.validRows} rows?`}
        message={
          preview === null
            ? ''
            : `${preview.validRows} ${labelFor(preview.importType).toLowerCase()} rows from ${preview.fileName} will be created${
                preview.errorRows > 0 ? `, and the ${preview.errorRows} rows with errors skipped` : ''
              }. The server checks every row again first and imports all of them or none. An import cannot be undone here.`
        }
        confirmLabel={preview === null ? 'Import' : `Import ${preview.validRows} rows`}
        loading={commit.isPending}
        onConfirm={() => {
          if (preview !== null) {
            commit.mutate(preview);
          }
        }}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

/** One server-reported count. */
function Figure({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  tone?: 'neutral' | 'success' | 'danger';
}) {
  const accent =
    tone === 'danger' ? 'var(--sms-danger)' : tone === 'success' ? 'var(--sms-success)' : 'var(--sms-accent)';
  return (
    <div className="border border-line bg-bg p-4">
      <div className="h-[3px] w-8" style={{ background: accent }} aria-hidden="true" />
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">{label}</p>
      <p className="mt-1 text-[26px] font-extrabold leading-none tabular-nums text-ink">{value}</p>
    </div>
  );
}

/** A server refusal, worded as the server worded it. */
function Refusal({ message, requestId }: { message: string; requestId?: string }) {
  return (
    <div role="alert" className="flex items-start gap-3 border border-line border-l-4 border-l-danger bg-danger-soft p-4">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">The server refused this</p>
        <p className="mt-0.5 break-words text-sm text-ink">{message}</p>
        {requestId !== undefined && <p className="mt-1 font-mono text-xs text-subtle">Reference: {requestId}</p>}
      </div>
    </div>
  );
}
